/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import creditCardType, { types as cardTypes } from 'credit-card-type';

import { dankort, laser, visaElectron } from './cardTypes';

/**
 * Adapted from https://www.npmjs.com/package/luhn / v2.4.1 / MIT license
 * @param {string} cardNumber
 * @returns boolean
 */
export function validateCardNumber(cardNumber) {
    const trimmed = String(cardNumber).replace(/[\s]/g, '');
    const length = trimmed.length;
    let odd = false;
    let total = 0;
    let calc;
    let calc2;

    if (!/^[0-9]+$/.test(trimmed)) {
        return false;
    }

    for (let i = length; i > 0; i -= 1) {
        calc = parseInt(trimmed.charAt(i - 1), 10);
        if (!odd) {
            total += calc;
        } else {
            calc2 = calc * 2;

            switch (calc2) {
                case 10:
                    calc2 = 1;
                    break;
                case 12:
                    calc2 = 3;
                    break;
                case 14:
                    calc2 = 5;
                    break;
                case 16:
                    calc2 = 7;
                    break;
                case 18:
                    calc2 = 9;
                    break;
                default:
                    break;
            }
            total += calc2;
        }
        odd = !odd;
    }

    return total !== 0 && total % 10 === 0;
}

/**
 * Given a credit card number in the format (XXXX XXXX XXXX...) return it as a string without any spaces
 * @param {*} number
 * @returns {string} number
 */
export const sanitizeNumber = (number) => {
    return number.toString().trim().replace(/ /g, '');
};

/**
 * Return the issuer of a given credit card number or `unknown` if the issuer can't be identified
 * @param {string|number} cardNumber
 * @returns {string} cardType
 */
export const getCardType = (cardNumber) => {
    const sanitizedNumber = sanitizeNumber(cardNumber);
    const potentialCardTypes = creditCardType(sanitizedNumber);

    if (potentialCardTypes.length === 1) {
        const firstResult = potentialCardTypes.shift();

        return firstResult.type;
    }

    return 'unknown';
};

/**
 * Configure the credit card types supported and return an array of valid types
 * @returns {string[]} validCardTypes
 */
export const setInitialValidCardTypes = () => {
    creditCardType.updateCard(cardTypes.MAESTRO, {
        patterns: [
            493698,
            [5000, 5018],
            [502000, 506698],
            [506779, 508999],
            [56, 59],
            63,
            67,
            6,
        ],
    });

    creditCardType.updateCard(cardTypes.HIPERCARD, {
        patterns: [384100, 384140, 384160, 606282, 637095, 637568],
    });

    creditCardType.addCard(dankort);
    creditCardType.addCard(laser);
    creditCardType.addCard(visaElectron);

    return Object.values(cardTypes).concat(['dankort', 'laser', 'visa-electron']);
};

/**
 * Provides a map of patterns to match for some card types
 */
export const cardTypesMap = {
    amex: ['amex', 'americanexpress', 'american-express'],
    dinersclub: ['diners', 'dinersclub', 'diners-club'],
    visaelectron: ['visaelectron', 'visa-electron'],
};

// cSpell:ignore dankort, americanexpress, dinersclub, visaelectron
